/**
 * Gate: make a multi-clinic owner choose a clinic before entering the app.
 *
 * Wraps the clinic app routes, NOT the onboarding ones. Keeping onboarding
 * outside matters — an owner setting up a freshly added branch is mid-flow with
 * a clinic already chosen, and interposing the picker there would fight
 * RequireAuth's onboarding redirect and bounce them between two screens.
 *
 * Renders the picker only when there is a real choice: an org owner, more than
 * one clinic, and no remembered preference. Staff — one clinic, no switcher —
 * pass straight through and never see it.
 */

import React, { useEffect } from "react";
import { Outlet } from "react-router-dom";

import { useClinic } from "../contexts/ClinicContext";
import SelectClinicPage from "../pages/clinic/SelectClinicPage";
import { getActiveClinicId } from "../utils/active-clinic";
import { getRememberedClinicId } from "../utils/clinic-preference";

export const RequireClinicSelection: React.FC = () => {
  const { isOrgOwner, clinics, isLoading, ensureLoaded } = useClinic();

  // Sign-in reaches here by navigate(), not a page load, and the context the
  // provider fetched on mount was fetched signed out. Load it for this user.
  // Here rather than on Amplify's signedIn event: by now the device is trusted,
  // so the request cannot 403 in the middle of the login page's OTP step.
  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  // Wait for org context before deciding. Rendering children first would flash
  // the dashboard — briefly showing one clinic's numbers — before replacing it
  // with the picker, which is exactly the "which clinic am I looking at?"
  // confusion this screen exists to remove.
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600" />
      </div>
    );
  }

  if (!isOrgOwner || clinics.length <= 1) return <Outlet />;

  // A remembered clinic skips the screen, but only while it is still reachable.
  // If it has left the org or been deactivated it is no longer in this list,
  // and silently falling back to some other clinic is the mislabelling problem
  // again — ask instead.
  const remembered = getRememberedClinicId();
  const rememberedIsValid =
    remembered !== null && clinics.some((c) => c.clinic_id === remembered);
  if (rememberedIsValid) return <Outlet />;

  // Already working in a clinic this session (a mid-session switch, or a
  // reload) — do not interrupt to re-ask.
  const active = getActiveClinicId();
  if (active && clinics.some((c) => c.clinic_id === active)) return <Outlet />;

  return <SelectClinicPage />;
};

export default RequireClinicSelection;
